import React, { useMemo } from 'react';
import { useGameStore } from '../stores/gameStore';
import { getTechForTier, techCost, formatNumber } from '@annopolis/shared';
import type { TechDef } from '@annopolis/shared';

const BRANCH_COLORS: Record<string, string> = {
  production: '#4af0c4',
  efficiency: '#818cf8',
  automation: '#fbbf24',
  warfare: '#f87171',
};

const BRANCH_ICONS: Record<string, string> = {
  production: '⚙',
  efficiency: '◈',
  automation: '⬡',
  warfare: '⚔',
};

export function TechPanel() {
  const { save, researchTech } = useGameStore();
  const techs = useMemo(() => getTechForTier(save.tier), [save.tier]);

  const grouped = useMemo(() => {
    const g: Record<string, TechDef[]> = {};
    for (const t of techs) {
      if (!g[t.branch]) g[t.branch] = [];
      g[t.branch].push(t);
    }
    // Sort by slot
    for (const b of Object.keys(g)) g[b].sort((a, b) => a.slot - b.slot);
    return g;
  }, [techs]);

  return (
    <div style={styles.panel}>
      <div style={styles.scroll}>
        {(Object.keys(grouped) as string[]).map((branch) => (
          <div key={branch} style={styles.branchSection}>
            <div style={{ ...styles.branchHeader, color: BRANCH_COLORS[branch] }}>
              <span style={styles.branchIcon}>{BRANCH_ICONS[branch]}</span>
              {branch.charAt(0).toUpperCase() + branch.slice(1)}
            </div>
            <div style={styles.techList}>
              {grouped[branch].map((def) => {
                const currentLevel = save.tech[def.id] ?? 0;
                const cost = techCost(def, currentLevel);
                const isMaxed = def.maxLevel !== -1 && currentLevel >= def.maxLevel;
                const canAfford = save.resources.knowledge >= cost && !isMaxed;
                const isUnlocked = checkTechUnlocked(def, save.units, save.tech);

                return (
                  <TechRow
                    key={def.id}
                    def={def}
                    currentLevel={currentLevel}
                    cost={cost}
                    canAfford={canAfford && isUnlocked}
                    isMaxed={isMaxed}
                    isLocked={!isUnlocked}
                    branchColor={BRANCH_COLORS[branch]}
                    onResearch={() => researchTech(def.id)}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TechRow({
  def, currentLevel, cost, canAfford, isMaxed, isLocked, branchColor, onResearch,
}: {
  def: TechDef;
  currentLevel: number;
  cost: number;
  canAfford: boolean;
  isMaxed: boolean;
  isLocked: boolean;
  branchColor: string;
  onResearch: () => void;
}) {
  const levelDisplay = def.maxLevel === 1 ? (currentLevel >= 1 ? '✓' : '—') : `Lv.${currentLevel}`;
  const nextLevelDisplay = def.maxLevel === 1 ? '' : def.maxLevel === -1 ? '∞' : `/${def.maxLevel}`;

  return (
    <div style={{ ...styles.techRow, opacity: isLocked ? 0.35 : 1 }}>
      <div style={styles.techLeft}>
        <div style={styles.techNameRow}>
          <span style={styles.techName}>{def.name}</span>
          <span style={{ ...styles.techLevel, color: isMaxed ? branchColor : '#4a6080' }}>
            {levelDisplay}{nextLevelDisplay}
          </span>
        </div>
        <span style={styles.techDesc}>{def.description}</span>
      </div>
      <button
        style={{
          ...styles.researchBtn,
          ...(isMaxed ? styles.researchBtnMaxed : canAfford ? { borderColor: `${branchColor}66`, color: branchColor } : styles.researchBtnDisabled),
        }}
        onClick={onResearch}
        disabled={!canAfford || isMaxed || isLocked}
      >
        {isMaxed ? 'MAX' : isLocked ? 'LOCKED' : `${formatNumber(cost)} Know.`}
      </button>
    </div>
  );
}

function checkTechUnlocked(def: TechDef, units: Record<number, number>, tech: Record<string, number>): boolean {
  if (!def.unlockRequires) return true;
  const req = def.unlockRequires;
  if (req.type === 'unit_owned' && req.unitId) return (units[req.unitId] ?? 0) >= (req.amount ?? 1);
  if (req.type === 'tech_level' && req.techId) return (tech[req.techId] ?? 0) >= (req.techLevel ?? 1);
  if (req.type === 'pvp_wins') return true; // checked server-side; optimistically unlock
  return true;
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: 'rgba(10,14,26,0.98)',
  },
  scroll: { flex: 1, overflowY: 'auto', padding: '4px 0' },
  branchSection: { marginBottom: 10 },
  branchHeader: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    fontWeight: 700,
    padding: '8px 12px 4px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  branchIcon: { fontSize: 13 },
  techList: {},
  techRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.03)',
    gap: 8,
  },
  techLeft: { flex: 1, minWidth: 0 },
  techNameRow: { display: 'flex', alignItems: 'center', gap: 6 },
  techName: { fontSize: 12, fontWeight: 500, color: '#c8d8f0' },
  techLevel: { fontSize: 10, fontWeight: 700 },
  techDesc: { fontSize: 10, color: '#4a6080', display: 'block', marginTop: 1 },
  researchBtn: {
    padding: '4px 10px',
    background: 'rgba(129,140,248,0.1)',
    border: '1px solid rgba(129,140,248,0.3)',
    borderRadius: 4,
    color: '#818cf8',
    fontSize: 10,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  researchBtnMaxed: {
    background: 'rgba(74,240,196,0.08)',
    border: '1px solid rgba(74,240,196,0.2)',
    color: '#4af0c4',
    cursor: 'default',
  },
  researchBtnDisabled: {
    background: 'rgba(74,96,128,0.05)',
    border: '1px solid rgba(74,96,128,0.15)',
    color: '#3a5070',
    cursor: 'not-allowed',
  },
};
